import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { type ServiceCard } from "@/components/ServiceTile";
import { ServicesCarousel } from "@/components/ServicesCarousel";
import { roofingTiles } from "@/data/serviceTiles";
import { RoofingMaterialsScroll } from "@/components/RoofingMaterialsScroll";
import { company } from "@/data/site";
import { SectionDecor } from "@/components/SectionDecor";
import { PageIntro } from "@/components/PageIntro";
import roofRepairImg from "@/assets/mckay/roof-repair.webp.asset.json";
import roofReplacementImg from "@/assets/mckay/roof-replacement.webp.asset.json";
import roofingServiceImg from "@/assets/mckay/roofing-service.webp.asset.json";
import gallery4 from "@/assets/gallery-new/6940e3a8b4f42026887ca890-2.webp.asset.json";
import gallery5 from "@/assets/gallery-new/6940e3a896e7535d5af6346b-2.webp.asset.json";
import gallery6 from "@/assets/gallery-new/6940e3a896e753e8e1f6346a-2.webp.asset.json";
import gallery8 from "@/assets/gallery-new/6940e3a8ab25944693304115-2.webp.asset.json";

export const Route = createFileRoute("/services/roofing")({
  head: () => ({
    meta: [
      { title: "Roofing Services in Southern California – McKay Roofing & Solar" },
      { name: "description", content: "Residential and commercial roofing in Southern California: repairs, replacements, emergency service, inspections and maintenance. Free estimates." },
      { property: "og:title", content: "Roofing Services – McKay Roofing & Solar" },
      { property: "og:description", content: "Residential and commercial roofing in Southern California. Repairs, replacements, inspections and maintenance." },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/services/roofing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Roofing",
          name: "Roofing Services – McKay Roofing & Solar",
          description: "Residential and commercial roofing across Southern California: repairs, replacements, emergency service, inspections and maintenance.",
          provider: {
            "@type": "RoofingContractor",
            name: "McKay Roofing & Solar",
            url: "https://mckay.steadyautogrowthdemo.com",
            telephone: "+1 858-541-1097",
            address: {
              "@type": "PostalAddress",
              addressLocality: "San Diego",
              addressRegion: "CA",
              addressCountry: "US",
            },
          },
          areaServed: { "@type": "AdministrativeArea", name: "Southern California" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Roofing Services",
            itemListElement: [
              "Roof Repair","Roof Replacement","Emergency Roof Repair","Roof Inspection",
              "Residential Roofing","Commercial Roofing","Roof Leak Repair","Flat Roof Repair",
              "Roof Maintenance","Insurance Claims Support",
            ].map((n) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n } })),
          },
        }),
      },
    ],
  }),
  component: RoofingPage,
});

const roofingServices: ServiceCard[] = roofingTiles.map((t) => ({ title: t.title, description: t.description, image: t.image, href: t.href }));

function RoofingPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <PageIntro storageKey="mckay-loader-roofing" variant="roof" />
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[85vh] w-full overflow-hidden text-primary-foreground">
          <HeroBackground src={roofingServiceImg.url} alt="McKay Roofing crew installing a new roof in Southern California" overlay="black-soft" />
          <div className="relative flex h-full min-h-[85vh] items-center">
            <div className="mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-bold leading-[1.1] sm:text-5xl lg:text-6xl text-balance">
                  Professional Roofing Services Built to Protect Your Property
                </h1>
                <p className="mt-6 max-w-2xl text-base text-primary-foreground/85 sm:text-lg">
                  Residential and commercial roofing across Southern California. Repairs, replacements, inspections and emergency service – from a local team that&apos;s been on roofs here since 1981.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                    <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">
                      <Phone className="mr-2 h-4 w-4" />
                      {company.phone}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="relative overflow-hidden bg-background">
          <SectionDecor variant="roof" side="right" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <div className="text-sm font-bold uppercase tracking-wider text-secondary">Our Roofing Work</div>
            <h2 className="mt-2 text-3xl font-bold text-primary sm:text-4xl text-balance">
              Reliable roofing solutions for homes and businesses
            </h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/85 sm:text-lg">
              McKay Roofing &amp; Solar handles the full range of roofing work across Southern California – from small repairs to full replacements, tile to shingle to flat commercial systems. Our crews focus on quality workmanship and long-term protection, and we respond quickly when a roof leak or storm damage can&apos;t wait.
            </p>
          </div>
        </section>

        {/* Services carousel */}
        <ServicesCarousel
          items={roofingServices}
          title="Roofing Services We Offer"
          subtitle="Every roofing service we provide across Southern California."
        />

        {/* Content sections */}
        <section className="bg-background overflow-x-clip">
          <div className="mx-auto max-w-6xl space-y-20 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <ContentRow
              image={roofingServiceImg.url}
              alt="McKay roofing crew working on a Southern California home"
              side="left"
              eyebrow="What We Do"
              title="Protecting your property from the top down"
            >
              <p>Your roof is one of the most important parts of your property, protecting your home or business from the elements year-round. Over time, exposure to harsh weather, aging materials, and everyday wear can lead to leaks, damage, and reduced performance. Addressing roofing issues early helps prevent costly repairs and extends the life of your roof.</p>
              <p>Our team begins by assessing the current condition of your roof and understanding your property&apos;s needs. We explain our findings clearly and provide practical recommendations, allowing you to make informed decisions with confidence and without unnecessary pressure.</p>
            </ContentRow>

            <ContentRow
              image={gallery8.url}
              alt="Storm-damaged roof needing repair"
              side="right"
              eyebrow="When Roofing Services Are Needed"
              title="Signs it's time to call a roofer"
            >
              <p>Roofing services are often needed when property owners notice signs of damage such as leaks, missing shingles, storm damage, sagging areas, or visible wear. In many cases, routine inspections also reveal issues before they become major problems, helping extend the lifespan of the roof and reduce future repair costs.</p>
              <p>Whether the roof has been affected by severe weather or has simply aged over time, addressing concerns early helps protect the structure, improve safety, and prevent more expensive damage. Regular maintenance and timely repairs keep your roofing system performing reliably year after year.</p>
            </ContentRow>

            {/* Middle CTA - booking */}
            <article className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 bg-primary px-6 py-12 text-primary-foreground sm:px-10">
              <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 text-center">
                <div className="text-sm font-bold uppercase tracking-wider text-primary-foreground/70">Roof Inspection Booking</div>
                <h2 className="text-3xl font-bold sm:text-4xl text-balance">
                  Worried about your roof? Book a free inspection
                </h2>
                <p className="max-w-2xl text-base text-primary-foreground/80">
                  Tell us what you&apos;re seeing and we&apos;ll schedule a time that works for you. You get a straight answer on the condition of your roof and a written estimate before any work starts.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">Book a Free Inspection <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" className="bg-white/10 text-primary-foreground hover:bg-white/20">
                    <a href={`tel:${company.phoneHref}`}><Phone className="mr-1 h-4 w-4" /> {company.phone}</a>
                  </Button>
                </div>
              </div>
            </article>


            <ContentRow
              image={roofRepairImg.url}
              alt="Roofer inspecting a shingle roof"
              side="left"
              eyebrow="Our Process"
              title="Simple, clear, and straightforward"
            >
              <p>Our process is designed to be straightforward and easy to understand. It begins with an inspection or consultation to assess the current condition of the roof or solar system. We then explain our findings and outline recommended next steps.</p>
              <p>Once a plan is approved, work is scheduled and completed with clear communication throughout the project. After completion, we review the results and address any remaining questions.</p>
            </ContentRow>

            <ContentRow
              image={gallery5.url}
              alt="Roofing project details"
              side="right"
              eyebrow="What Our Services Include"
              title="Consistent results, clear expectations"
            >
              <p>While each project is different, most roofing and solar services include a similar set of steps to ensure consistent results and clear expectations.</p>
              <ul className="mt-4 space-y-3">
                {[
                  "Inspection and evaluation",
                  "Clear explanation of findings and options",
                  "Repair, replacement, or installation work",
                  "Use of appropriate materials and equipment",
                  "Cleanup and final review",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-secondary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ContentRow>

            <ContentRow
              image={gallery6.url}
              alt="Completed McKay roofing project"
              side="left"
              eyebrow="Residential and Commercial Properties"
              title="Built for homes and businesses alike"
            >
              <p>Residential and commercial properties have different requirements, and our services are structured to support both. Residential projects often focus on protecting the home and managing long-term maintenance, while commercial projects may involve larger systems and coordinated scheduling.</p>
              <p>Our experience with both property types allows us to adapt our approach based on the scope of work and the needs of the client.</p>
            </ContentRow>
          </div>
        </section>

        {/* Materials we install */}
        <RoofingMaterialsScroll heading="Materials We Install" />

        {/* CTA */}
        <section className="relative overflow-hidden bg-background">
          <SectionDecor variant="house" side="left" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Need Help With Your Roof?</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Free, no-pressure estimates. We&apos;ll take a look, explain what we find, and lay out your options clearly.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">
                  <Phone className="mr-2 h-4 w-4" />
                  {company.phone}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ContentRow({
  image,
  alt,
  side,
  eyebrow,
  title,
  children,
}: {
  image: string;
  alt: string;
  side: "left" | "right";
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
      <div className={side === "right" ? "lg:order-2" : ""}>
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="aspect-[4/3] w-full rounded-2xl object-cover shadow-elegant"
        />
      </div>
      <div>
        <div className="text-sm font-bold uppercase tracking-wider text-secondary">{eyebrow}</div>
        <h2 className="mt-2 text-3xl font-bold text-primary sm:text-4xl text-balance">{title}</h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-foreground/85 sm:text-lg">
          {children}
        </div>
      </div>
    </div>
  );
}